/**
 * The intermediary repo (RFC 0275, ported from Patchwork's `@patchwork/isolation`).
 *
 * A sandboxed app gets collaborative editing by syncing DOCUMENTS over a
 * MessagePort and building its own handle from its own repo. The handle never
 * crosses the boundary; the document does. That is what makes editing possible
 * inside a sandbox, and it is also what makes a gate mandatory: a repo
 * connection serves whatever it is asked for.
 *
 * WHY A SEPARATE REPO AND NOT A POLICY ON OURS. We could gate the host's own
 * repo per peer. Patchwork does not, and the reason is worth copying: an
 * ephemeral in-memory repo only ever HOLDS the documents that passed the gate,
 * so a bug in the policy cannot leak a document the intermediary never fetched.
 * Gating a repo that holds everything makes the policy the only thing standing
 * between an app and the project. This way the policy is the second line and
 * possession is the first.
 *
 * NO STORAGE, deliberately. The intermediary is a relay, not a cache. Giving it
 * IndexedDB would persist an app's granted documents under the host's origin
 * and outlive the grant that justified them.
 *
 *   host repo ──portA── intermediary ──portB── sandboxed app
 *                        (gate here)
 *
 * The app receives portB and nothing else: no host repo, no storage, no
 * subduction connection, no ticket.
 *
 * NOT VERIFIED IN A BROWSER YET. The gate below is unit-tested; this wiring is
 * not. Treat "it constructs" as meaning nothing until a real app has synced a
 * real document through it, which is the standard this session has had to learn
 * repeatedly.
 */

import { Repo } from '@automerge/automerge-repo'
import { MessageChannelNetworkAdapter } from '@automerge/automerge-repo-network-messagechannel'
import { allowsSync, type SyncGateState } from './syncGate'

export interface SandboxSync {
  /** Transferred into the iframe. The app's ONLY route to any document. */
  guestPort: MessagePort
  /** Tear down both channels. Called when the app unmounts. */
  stop: () => void
}

export function createSandboxSync(
  hostRepo: Repo,
  gate: SyncGateState,
  /** Who is being refused, for the host-side log. An app name reads better than
   *  a peer id in a console that a person is trying to debug from. */
  label = 'app',
): SandboxSync {
  const toHost = new MessageChannel()
  const toGuest = new MessageChannel()

  // The host end. The intermediary is an ordinary peer to the host repo, which
  // is why it can ask for the documents it needs.
  const hostAdapter = new MessageChannelNetworkAdapter(toHost.port1)
  hostRepo.networkSubsystem.addNetworkAdapter(hostAdapter)

  const hostPeerId = hostRepo.networkSubsystem.peerId

  const intermediary = new Repo({
    network: [
      new MessageChannelNetworkAdapter(toHost.port2),
      new MessageChannelNetworkAdapter(toGuest.port1),
    ],
    // No storage: see the header.
    sharePolicy: async (peerId, documentId) => {
      // UPSTREAM IS UNGATED. Asking the host for a document is how the
      // intermediary obtains anything at all; the gate is about what leaves
      // toward the app, not what arrives from the project.
      if (peerId === hostPeerId) return true
      // DOWNSTREAM IS THE BOUNDARY. Everything else is the sandboxed app, and
      // it gets what the gate allows and nothing else. A missing documentId
      // fails closed rather than defaulting to share.
      if (!documentId) return false
      const allowed = allowsSync(gate, documentId)
      if (!allowed) {
        // THE ONLY PLACE THIS CAN BE SAID. The gate refuses by not serving, so
        // from inside the app a denial and a slow document are the same signal
        // — deliberately, because the app must not learn which. That makes a
        // real denial undebuggable from outside too, unless the one party that
        // knows says so. The intermediary knows. This costs nothing and cannot
        // leak: it is host-side, and the app never sees it.
        console.info(
          `[sandbox] refused ${documentId} for ${label}: ` +
            (gate.ready
              ? gate.deny.has(documentId)
                ? 'denylisted'
                : 'not in allowlist'
              : 'denylist not ready yet'),
        )
      }
      return allowed
    },
  })

  return {
    guestPort: toGuest.port2,
    stop: () => {
      // Ports first, so nothing is mid-flight while the repo is torn down.
      try {
        toGuest.port1.close()
        toGuest.port2.close()
        toHost.port1.close()
        toHost.port2.close()
      } catch {
        // A closed port throwing on close must not strand the shutdown.
      }
      void intermediary.shutdown?.()
    },
  }
}
